import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useWorkspaceModules } from '../../../hooks/useWorkspaceModules';
import { useAuth } from '../../../context/AuthContext';
import { useUI } from '../../../context/UIContext';
import {
  Check,
  X,
  Loader,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

export function WorkspaceModulesManager() {
  const { activeWorkspace } = useAuth();
  const { showToast } = useUI();
  const { modules, enabledModules, isLoading, isUpdating, error, updateModules } =
    useWorkspaceModules();

  const [localEnabledModules, setLocalEnabledModules] = useState<string[]>(
    enabledModules,
  );
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const hasChanges = JSON.stringify(localEnabledModules.sort()) !==
    JSON.stringify(enabledModules.sort());

  const toggleModule = (moduleId: string) => {
    setLocalEnabledModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId],
    );
  };

  const handleSave = async () => {
    try {
      await updateModules(localEnabledModules);
      showToast('Módulos atualizados com sucesso!', 'success');
    } catch (err) {
      showToast('Erro ao atualizar módulos', 'error');
    }
  };

  const handleCancel = () => {
    setLocalEnabledModules(enabledModules);
  };

  if (!activeWorkspace) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <p className="text-slate-600">Workspace não encontrado</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader className="w-8 h-8 text-indigo-600 mx-auto mb-3 animate-spin" />
          <p className="text-slate-600">Carregando módulos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2">
          Módulos do Workspace
        </h1>
        <p className="text-slate-600">
          Gerencie quais módulos estão habilitados no workspace{' '}
          <span className="font-semibold text-slate-900">
            {activeWorkspace.name}
          </span>
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-900">Dica</p>
          <p className="text-sm text-blue-800 mt-1">
            O módulo Dashboard sempre está disponível. Você pode habilitar ou
            desabilitar outros módulos conforme necessário.
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-900">Erro</p>
            <p className="text-sm text-red-800 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((module) => {
          const isEnabled = localEnabledModules.includes(module.id);
          const isDashboard = module.id === 'dashboard';

          return (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`rounded-lg border-2 transition-all p-4 cursor-pointer ${
                isEnabled
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              } ${!module.canAccess ? 'opacity-60 cursor-not-allowed' : ''}`}
              onClick={() =>
                !isDashboard && module.canAccess && toggleModule(module.id)
              }
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 mb-1">
                    {module.name}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {module.description}
                  </p>
                </div>

                {/* Checkbox */}
                <div className="ml-2 flex-shrink-0">
                  {isDashboard ? (
                    <div className="w-6 h-6 rounded-lg bg-indigo-600 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  ) : !module.canAccess ? (
                    <div className="w-6 h-6 rounded-lg border-2 border-slate-300 flex items-center justify-center bg-slate-100">
                      <X className="w-4 h-4 text-slate-400" />
                    </div>
                  ) : (
                    <div
                      className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                        isEnabled
                          ? 'bg-indigo-600 border-indigo-600'
                          : 'border-slate-300 bg-white'
                      }`}
                    >
                      {isEnabled && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Expandable details */}
              {!isDashboard && (
                <motion.button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedModule(
                      expandedModule === module.id ? null : module.id,
                    );
                  }}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1 mt-2"
                >
                  {expandedModule === module.id ? (
                    <>
                      <ChevronUp className="w-3 h-3" /> Ocultar detalhes
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3" /> Detalhes
                    </>
                  )}
                </motion.button>
              )}

              {/* Status badges */}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {isDashboard && (
                  <span className="text-xs font-bold px-2 py-1 rounded-full bg-indigo-600 text-white">
                    Sempre Ativo
                  </span>
                )}
                {!module.canAccess && (
                  <span className="text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                    Sem Permissão
                  </span>
                )}
                {module.requiredPermission && (
                  <span className="text-xs font-normal text-slate-500">
                    Requer: {module.requiredPermission}
                  </span>
                )}
              </div>

              {/* Expanded details */}
              <AnimatePresence>
                {expandedModule === module.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-600"
                  >
                    <p>
                      ID: <span className="font-mono">{module.id}</span>
                    </p>
                    {module.requiredPermission && (
                      <p className="mt-1">
                        Requer: <span className="font-mono text-slate-700">{module.requiredPermission}</span>
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Save/Cancel Buttons */}
      {hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-end gap-3 sticky bottom-4 bg-white rounded-lg p-4 border border-slate-200 shadow-lg"
        >
          <button
            onClick={handleCancel}
            disabled={isUpdating}
            className="px-4 py-2 rounded-lg font-semibold text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isUpdating}
            className="px-6 py-2 rounded-lg font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isUpdating && <Loader className="w-4 h-4 animate-spin" />}
            Salvar Alterações
          </button>
        </motion.div>
      )}
    </div>
  );
}
